import React, { useEffect, useMemo, useState } from 'react';
import { Award, Plus, RefreshCw } from 'lucide-react';
import http from '../api/http';
import BlueHeader from '../components/BlueHeader';
import { Avatar, Field, IdChip, ListCard, Modal, RowActions, Table, inputCls, rowCls, tdCls } from '../components/ui';

const Certificats = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await http.get('/api/type-certificat');
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.filter((i) => (i.libelle_certificat || '').toLowerCase().includes(q)) : data;
  }, [data, search]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce type de certificat ?')) return;
    try {
      await http.delete(`/api/type-certificat/${id}`);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) await http.put(`/api/type-certificat/${editingItem.idtype_certificat}`, formData);
      else await http.post('/api/type-certificat', formData);
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  return (
    <div className="min-h-full bg-slate-50 dark:bg-slate-950">
      <BlueHeader
        title="Certificats"
        subtitle="Gestion des certificats"
        icon={Award}
        showBack={false}
        actions={[
          { label: 'Créer', icon: Plus, onClick: handleCreate, primary: true },
          { label: 'Actualiser', icon: RefreshCw, onClick: fetchData, primary: false },
        ]}
      />

      <div className="p-6">
        <ListCard
          count={filtered.length} singular="certificat affiché" plural="certificats affichés"
          search={search} onSearch={setSearch} placeholder="Rechercher un certificat"
          loading={loading} hasData={data.length > 0} icon={Award}
          emptyTitle="Aucun certificat pour le moment" emptyText="Créez votre premier type de certificat pour commencer."
          onCreate={handleCreate} createLabel="Créer un certificat" createIcon={Plus}
        >
          <Table heads={['Certificat', 'Actions']}>
            {filtered.map((item) => (
              <tr key={item.idtype_certificat} className={rowCls}>
                <td className={tdCls}>
                  <div className="flex items-center gap-3">
                    <Avatar icon={Award} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.libelle_certificat}</p>
                      <p className="mt-0.5"><IdChip>#{item.idtype_certificat}</IdChip></p>
                    </div>
                  </div>
                </td>
                <td className={tdCls}>
                  <RowActions onEdit={() => handleEdit(item)} onDelete={() => handleDelete(item.idtype_certificat)} />
                </td>
              </tr>
            ))}
          </Table>
        </ListCard>
      </div>

      {showModal && (
        <Modal
          title={editingItem ? 'Modifier le certificat' : 'Créer un certificat'}
          subtitle={editingItem ? editingItem.libelle_certificat : 'Renseignez le type de certificat'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          confirmLabel={editingItem ? 'Enregistrer' : 'Créer'}
        >
          <div className="space-y-5 p-6">
            <Field label="Libellé du certificat">
              <input
                type="text"
                className={inputCls}
                value={formData.libelle_certificat || ''}
                onChange={(e) => setFormData({ ...formData, libelle_certificat: e.target.value })}
                required
              />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Certificats;